// pages/QuoteSystemSettings.jsx
import React, { useState, useEffect } from "react";
import {
  Link2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader,
  RefreshCw,
} from "lucide-react";
import QuoteSystemIntegrationService from "../../services/quoteSystemIntegrationService";
import { useTheme } from "../../contexts/ThemeContext";

const QuoteSystemSettings = () => {
  const { isDarkMode } = useTheme();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const result = await QuoteSystemIntegrationService.getConnectionStatus();
      setStatus(result.data);
    } catch (err) {
      console.error("Error loading status:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError("");
      setSuccess("");

      const result = await QuoteSystemIntegrationService.establishConnection();

      setSuccess("Successfully connected to Quote System");
      await loadStatus();
    } catch (err) {
      setError(err.message || "Failed to connect");
    } finally {
      setConnecting(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setError("");
      setSuccess("");

      const result = await QuoteSystemIntegrationService.testConnection();

      if (result.success) {
        setSuccess("Connection test successful");
      } else {
        setError("Connection test failed");
      }
    } catch (err) {
      setError(err.message || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm("Are you sure you want to disconnect the Quote System?")
    ) {
      return;
    }

    try {
      setLoading(true);
      const result = await QuoteSystemIntegrationService.disconnect();

      if (result.success) {
        setSuccess("Disconnected successfully");
        await loadStatus();
      }
    } catch (err) {
      setError(err.message || "Disconnect failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !status) {
    return (
      <div
        className={`rounded-lg border p-6 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="flex items-center justify-center py-12">
          <Loader
            className={`animate-spin ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
            size={32}
          />
        </div>
      </div>
    );
  }

  const isConnected = status?.is_connected;

  return (
    <div
      className={`rounded-lg border p-6 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3
            className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            <Link2 size={20} />
            Quote System Integration
          </h3>
          <p
            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Connect to the Quote System for automatic contract sync
          </p>
        </div>

        <button
          onClick={loadStatus}
          disabled={loading}
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${isDarkMode ? "bg-red-500/10 border border-red-500/30" : "bg-red-50 border border-red-200"}`}
        >
          <AlertCircle
            size={16}
            className="text-red-400 mt-0.5 flex-shrink-0"
          />
          <span
            className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-600"}`}
          >
            {error}
          </span>
        </div>
      )}

      {success && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${isDarkMode ? "bg-green-500/10 border border-green-500/30" : "bg-green-50 border border-green-200"}`}
        >
          <CheckCircle
            size={16}
            className="text-green-400 mt-0.5 flex-shrink-0"
          />
          <span
            className={`text-sm ${isDarkMode ? "text-green-300" : "text-green-600"}`}
          >
            {success}
          </span>
        </div>
      )}

      {/* Connection Status */}
      <div
        className={`mb-6 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
      >
        <div className="flex items-center justify-between mb-3">
          <span
            className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            Status
          </span>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <CheckCircle size={16} className="text-green-400" />
                <span className="text-green-400 text-sm font-medium">
                  Connected
                </span>
              </>
            ) : (
              <>
                <XCircle size={16} className="text-gray-400" />
                <span className="text-gray-400 text-sm font-medium">
                  Not Connected
                </span>
              </>
            )}
          </div>
        </div>

        {isConnected && status && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Company ID:
              </span>
              <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                {status.company_id}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Last Synced:
              </span>
              <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                {status.last_synced_at
                  ? new Date(status.last_synced_at).toLocaleString()
                  : "Never"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Token Expires:
              </span>
              <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                {new Date(status.token_expires_at).toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sync Statistics */}
      {isConnected && status?.sync_stats?.last_sync && (
        <div
          className={`mb-6 p-4 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
        >
          <h4
            className={`text-sm font-medium mb-3 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            Last Sync Statistics
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Synced:
              </span>
              <span className="ml-2 text-green-400 font-medium">
                {status.sync_stats.last_sync.stats.synced}
              </span>
            </div>
            <div>
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Failed:
              </span>
              <span className="ml-2 text-red-400 font-medium">
                {status.sync_stats.last_sync.stats.failed}
              </span>
            </div>
            <div>
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Skipped:
              </span>
              <span className="ml-2 text-yellow-400 font-medium">
                {status.sync_stats.last_sync.stats.skipped}
              </span>
            </div>
            <div>
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                Total:
              </span>
              <span
                className={`ml-2 font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {status.sync_stats.last_sync.stats.total_processed}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {!isConnected ? (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white disabled:opacity-50"
          >
            {connecting ? (
              <>
                <Loader size={16} className="animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Link2 size={16} />
                Connect to Quote System
              </>
            )}
          </button>
        ) : (
          <>
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
            >
              {testing ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Test Connection
                </>
              )}
            </button>

            <button
              onClick={handleDisconnect}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors text-white"
            >
              <XCircle size={16} />
              Disconnect
            </button>
          </>
        )}
      </div>

      {/* Info Box */}
      <div
        className={`mt-6 p-4 rounded-lg ${isDarkMode ? "bg-blue-500/10 border border-blue-500/30" : "bg-blue-50 border border-blue-200"}`}
      >
        <div className="flex items-start gap-2">
          <AlertCircle
            size={16}
            className="text-blue-400 mt-0.5 flex-shrink-0"
          />
          <div
            className={`text-sm ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
          >
            <p className="font-medium mb-1">Automatic Sync</p>
            <p>
              Once connected, the Quote System will automatically sync signed
              agreements every 30 minutes. Signed contract PDFs will be linked
              to opportunities.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteSystemSettings;
